import type { ImageScenario } from '../game/imageTypes';

/**
 * Level 3 -- a Jira workflow editor with the "Done" transition selected.
 *
 * ============================ DRAFT CLASSIFICATION ==========================
 * Regions are measured by tools/find_arrows.py and should be correct. Which
 * arrows are real defects is a draft and needs confirming before release.
 * ============================================================================
 *
 * The teaching point of this level is a distinction, not a rule: a global
 * "Any status ->" transition into an intermediate state is an ordinary
 * convenience, while the same thing into a terminal state dismantles the
 * process. Several decoys exist purely to make that line worth finding.
 *
 * Image is 2000 x 1063. All regions are percentages of those dimensions.
 */
export const workflowTransition: ImageScenario = {
  id: 'jira-workflow-transition',
  title: 'A workflow with no way to fail',
  subtitle: 'Workflow editor · Done transition',
  brief:
    'The workflow behind a board, with the Done transition open on the right. Eleven arrows point at pieces of the flow and its rules. Most of what looks alarming here is ordinary Jira; one combination genuinely means work can be declared finished without ever being done.',

  source: {
    toolId: 'jira',
    screen: 'Workflow editor › transition rules',
    kind: 'screenshot',
    note: 'Captured from a ReleaseTEAM test instance, annotated with red arrows.',
  },

  image: {
    src: '/scenarios/workflow-transition.webp',
    width: 2000,
    height: 1063,
    alt: 'Jira workflow editor showing statuses Backlog, Selected for Development, In Progress and Done, each reachable by an "Any" transition, with the Done transition panel open showing empty Restrict transition, Request input and Validate details rules and one Perform action that sets Resolution to Done.',
  },

  arrows: [
    {
      id: 'show-transition-labels',
      label: '1',
      region: { x: 62.52, y: 6.68, w: 3.0, h: 7.9 },
      description: 'Arrow pointing at the unchecked "Show transition labels" checkbox',
      decoyNote:
        'This is a view toggle for the diagram, not a workflow setting. Ticking it draws the transition names on the arrows so the canvas is easier to read; leaving it off changes nothing about how work moves. Nothing a player does here could ever affect a single work item.',
    },
    {
      id: 'transition-name',
      label: '2',
      region: { x: 79.25, y: 8.75, w: 4.05, h: 6.68 },
      description: 'Arrow pointing at the transition Name field reading "Done"',
      decoyNote:
        'Naming the transition after the status it leads to is the clearest convention there is. A transition called "Done" that moves work to Done needs no improvement.',
    },
    {
      id: 'start-node',
      label: '3',
      region: { x: 4.48, y: 38.76, w: 3.0, h: 6.68 },
      description: 'Arrow pointing at the START node feeding into Backlog',
      decoyNote:
        'Every Jira workflow has exactly one start node, and it must point at the initial status. This one goes to Backlog, which is where newly created work belongs. It is structurally required and correct.',
    },
    {
      id: 'restrict-transition',
      label: '4',
      region: { x: 71.05, y: 33.44, w: 7.0, h: 5.0 },
      description: 'Arrow pointing at the empty "Restrict transition" rule',
    },
    {
      id: 'request-input',
      label: '5',
      region: { x: 72.7, y: 40.68, w: 5.55, h: 5.0 },
      description: 'Arrow pointing at the empty "Request input" rule',
      decoyNote:
        'Request input prompts the user for a field value as they transition — useful for things like a release note or a rejection reason, but entirely optional. Its absence is a missing convenience, not a missing control.',
    },
    {
      id: 'any-into-selected',
      label: '6',
      region: { x: 35.62, y: 53.76, w: 3.0, h: 5.0 },
      description:
        'Arrow pointing at the "Any" transition badge leading into Selected for Development',
      decoyNote:
        'A global "Any status → Selected for Development" is an ordinary convenience: it lets work be pulled back into the queue from wherever it stalled without walking it backwards through the board. Because this is not a terminal status, nothing is finalised by taking the shortcut — the work still has to leave through a later transition.',
    },
    {
      id: 'any-into-done',
      label: '7',
      region: { x: 56.8, y: 53.52, w: 3.0, h: 5.0 },
      description:
        'Arrow pointing at the highlighted "Any" transition badge leading into Done',
    },
    {
      id: 'validate-details',
      label: '8',
      region: { x: 72.45, y: 48.25, w: 5.8, h: 5.0 },
      description: 'Arrow pointing at the empty "Validate details" rule',
    },
    {
      id: 'backlog-node',
      label: '9',
      region: { x: 14.2, y: 57.57, w: 3.0, h: 10.25 },
      description: 'Arrow pointing at the Backlog status node',
      decoyNote:
        'Backlog is the first status in the flow and is reachable from START. Nothing about the node itself is misconfigured — the problem on this screen is what can be reached from it, not that it exists.',
    },
    {
      id: 'in-progress-node',
      label: '10',
      region: { x: 42.6, y: 56.44, w: 3.0, h: 5.93 },
      description: 'Arrow pointing at the In Progress status node',
      decoyNote:
        'A conventional middle status, sitting where it should between Selected for Development and Done. The flow shape is fine; what is missing is anything forcing work to pass through it.',
    },
    {
      id: 'resolution-action',
      label: '11',
      region: { x: 87.3, y: 69.71, w: 3.1, h: 7.9 },
      description:
        'Arrow pointing at the action that automatically sets Resolution to "Done"',
      decoyNote:
        'This is exactly right, and it is the one thing on this panel that is properly configured. A terminal transition should stamp a resolution automatically, because work left with an empty Resolution shows as unresolved forever and quietly poisons every filter and burndown that keys off it.',
    },
  ],

  defects: [
    {
      id: 'd-any-into-done',
      arrowId: 'any-into-done',
      title: 'Anything can jump straight to Done',
      severity: 'critical',
      explanation:
        'The Done transition is configured as "Any status → Done", so an item sitting untouched in Backlog can be closed in a single click without ever passing through Selected for Development, In Progress or any review. Compare it with the identical shortcut into the intermediate statuses, which is harmless: those are states work must still leave, whereas Done is terminal and stamps a resolution on the way in. Every process guarantee the board appears to offer is optional.',
      fix: 'Replace the global transition with explicit paths into Done — realistically from In Progress, and from a review status if you have one. Keep global transitions for intermediate states, where the shortcut costs nothing.',
    },
    {
      id: 'd-no-validation',
      arrowId: 'validate-details',
      title: 'Nothing is checked before work is called Done',
      severity: 'major',
      explanation:
        'Validate details is empty, so the transition into a terminal state asks for nothing: no resolution choice, no required field, no confirmation that the work actually happened. On its own that is merely permissive. Combined with a transition reachable from any status, it means an item can be created and closed without a single field ever being filled in.',
      fix: 'Add a validator for whatever "finished" genuinely requires — commonly an assignee and a non-empty resolution, and for many teams a linked pull request.',
    },
    {
      id: 'd-no-restriction',
      arrowId: 'restrict-transition',
      title: 'No restriction on who may close work',
      severity: 'major',
      explanation:
        'Restrict transition is empty, so the ability to move an item to Done is granted to everyone who can edit the project. Left alone this is a defensible default for a trusting team. Alongside a global transition and no validation, it completes the picture: any user, from any status, with no checks.',
      fix: 'Restrict the transition to the roles that genuinely decide when work is finished — typically the assignee plus a reviewer role — rather than everyone with edit rights.',
    },
  ],
};
